import * as React from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { Box, Button, Icon, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import { zodResolver } from '@hookform/resolvers/zod'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { useAddShiftsFromUnassigned } from 'modules/shift_management/hooks/useAddShiftsFromUnassigned'
import useDateRangeSelection from 'modules/shift_management/hooks/useDateRangeSelection'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectableEmployees } from 'modules/shift_management/hooks/useSelectableEmployees'
import { buildAddShiftsInputParameters } from 'modules/shift_management/lib/buildAddShiftsInput'
import { Form } from 'modules/shift_management/lib/forms/AssignEmployees/form'
import { AssignEmployeesSchemaType } from 'modules/shift_management/lib/forms/AssignEmployees/schema'
import { UnassignedShift } from 'modules/shift_management/lib/types'

interface Props {
  unassignedShift: UnassignedShift
  totalAssignedShifts: number
  onClose: () => void
}

const AssignEmployeesModal = ({ unassignedShift, totalAssignedShifts, onClose }: Props) => {
  const [{ view }] = useSchedule()
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const description = dateToString(unassignedShift.startAt, 'ui_long')
  const date = dateToString(unassignedShift.startAt, 'backend')

  const originalCounter = unassignedShift.counter
  const employeesNeeded = originalCounter - totalAssignedShifts
  const [open, toggle] = useToggle(false)

  const { loading: loadingEmployees, selectableEmployeeIds } = useSelectableEmployees(
    view.startAt,
    view.endAt
  )

  const { defaultValues, schema } = Form()
  const form = useForm<AssignEmployeesSchemaType>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: defaultValues,
    shouldUnregister: false,
  })

  const { watch, setValue, formState } = form
  const employeeSelection = watch('employeeSelection')

  const daysByEmployee = useDateRangeSelection(employeeSelection.ids, date, date, '', true)

  const shifts = buildAddShiftsInputParameters({
    view,
    hasSelection: false,
    defaultAttributesByDate: {},
    name: null,
    unparsedStartTime: unassignedShift.startTime,
    unparsedEndTime: unassignedShift.endTime,
    daysByRow: daysByEmployee,
    defaultShiftId: unassignedShift.defaultShiftId,
    extraHours: unassignedShift.extraHours,
    notes: unassignedShift.notes,
    locationId: unassignedShift.locationId,
    workAreaId: unassignedShift.workAreaId,
    color: null,
    templateWeekId: null,
    unassignedShiftId: unassignedShift.id,
    state: 'draft',
    leaveId: undefined,
    companyId,
  })

  const { onSubmit, submitting } = useAddShiftsFromUnassigned({
    shifts,
    weekDays: '',
    isDefaultShift: !!unassignedShift.defaultShiftId,
    unassignedShiftCounter: unassignedShift.counter,
  })

  const disabled =
    !formState.isDirty || employeeSelection.ids?.length > employeesNeeded || submitting

  return (
    <FormProvider {...form}>
      <Modal
        illustration="tasks_management"
        title={i18n.t('shift_management.shift_menu_options.assign_employees')}
        description={description}
        onSubmit={onSubmit}
        onClose={onClose}
        footer={
          <>
            <Button onClick={onClose} type="secondary">
              {i18n.t('common.cancel')}
            </Button>
            <Button disabled={disabled} submit>
              {i18n.t('common.save')}
            </Button>
          </>
        }
      >
        {loadingEmployees ? (
          <Loading />
        ) : (
          <Box gap="s24">
            <Box gap="s4">
              <Box flexDirection="row" gap="s4" alignItems="center">
                <Text.Normal size="200" color="grey1000" weight="medium">
                  {i18n.t('shift_management.coverage_section.unassigned_shift.amount_employees')}
                </Text.Normal>
                <Tooltip.Alt
                  attachment="top center"
                  open={open}
                  toggle={toggle}
                  label={i18n.t(
                    'shift_management.coverage_section.unassigned_shift.amount_employees_description'
                  )}
                >
                  <Icon.Small icon="info" encircled="grey800" />
                </Tooltip.Alt>
              </Box>
              <TextField value={employeesNeeded.toString()} readonly />
            </Box>
            <EmployeeSelectorV2
              onChange={(value) => {
                if (!value || value.type !== 'manual') return

                setValue('employeeSelection', value, {
                  shouldDirty: true,
                })
              }}
              multiple
              forcedFilters={{ employeeIds: selectableEmployeeIds }}
              options={{ hideLabel: true }}
              value={employeeSelection.ids ?? undefined}
            />
          </Box>
        )}
      </Modal>
    </FormProvider>
  )
}

export default observer(AssignEmployeesModal)
